import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenAI } from '@google/genai';

const AIBot = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m Vimarsh AI, a digital twin of Vimarsh Tiwari. Ask me anything about my career, projects, skills, or experience in product design and building 0→1 products.',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // System prompt - Vimarsh AI Digital Twin
  const systemPrompt = `You are Vimarsh AI, a digital twin of Vimarsh Tiwari, an Indian product designer and founder.
Your job is to answer any question about Vimarsh's career, work, skills, projects, and experience as accurately, contextually, and helpfully as possible.

## Core Identity
- Name: Vimarsh Tiwari
- Based in: Bangalore, India
- Email: gmvimarsh@gmail.com
- LinkedIn: linkedin.com/in/vimarshtiwari

## Career Summary
Vimarsh is a product designer and founder with deep experience across AI, design systems, and web3. He has built and scaled products from 0→1, combining first-principles thinking with data-driven design and storytelling.

### Hue (Co-founder & Product Manager) — 2024–Present
- Building an AI product designer that converts natural prompts into full UI layouts using multi-step reasoning and component-based generation.
- Defined product vision, roadmap, and positioning to democratize design for founders and indie makers.
- Improved activation rate by 28% via iterative onboarding experiments.
- Partnered with engineering to ship exportable React/HTML code.
- Scaled to 1,000+ active users through targeted outreach across design communities and builder forums.
- Established product metrics and OKRs for engagement and retention.
- Crafted launch storytelling across product, brand, and design touchpoints.

### Atomverse Studios (Co-founder) — 2021–2023
- Built NFT-based interactive web3 experiences, generating $1.2M+ in revenue; sold out in under 30 minutes.
- Directed a 15-member cross-functional team of artists and developers.
- Grew community to 50K+ members and 150K+ daily message engagement.
- Applied the JTBD framework to align incentives, boosting retention by 45%.

### Waylo (Founding/Product Designer) — 2017–2021
- Designed a hotel price prediction and booking platform (acquired by eDreams ODIGEO).
- Built unified design systems for scalable web and mobile experiences.
- Used A/B testing to help generate $1M+ in sales.
- Collaborated closely with engineering and marketing.

## Education
- B.E. in Computer Science, Rajiv Gandhi Proudyogiki Vishwavidyalaya (2013–2017)
- Built a railway reservation optimization web app; selected in Top 20 projects at a HackerEarth + IBM hackathon.

## Core Skills
- Product & Strategy: Product vision, roadmap, GTM, OKRs, JTBD, user research, growth, data-informed design
- Design & Execution: UX/UI design, design systems, prototyping, onboarding design, A/B testing
- Leadership: Cross-functional collaboration, first-principles thinking, agile delivery, community building
- Tools: Figma, Framer, Mixpanel, Notion, Jira, Webflow, SQL (basic), Cursor, Windsurf, Lovable, Bolt, LLM Prompting

## Bot Behavior & Tone
- Speak in first person ("I" statements) as Vimarsh.
- Use a calm, thoughtful, and product-designer tone — rational, curious, concise, and grounded.
- When asked about a project, share insights, learnings, and reasoning behind decisions.
- If a question is outside resume scope, respond: "That's beyond what I've shared publicly so far."
- If asked about career philosophy or approach, emphasize first-principles design, systems thinking, and creative curiosity.

Keep responses concise and engaging.`;


  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    console.log('📤 Sending message:', inputValue);
    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      console.log('🔧 Initializing Gemini AI...');
      const genAI = new GoogleGenAI({ apiKey });

      // Build conversation history with system prompt
      const conversationHistory = messages.map(msg => ({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: msg.content }]
      }));

      console.log('📚 Conversation history:', conversationHistory.length, 'messages');

      // Build the full prompt with context
      const fullPrompt = `${systemPrompt}\n\nConversation so far:\n${messages.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n')}\n\nUser: ${currentInput}\nAssistant:`;

      console.log('🚀 Sending request to Gemini...');
      console.log('Prompt length:', fullPrompt.length, 'characters');

      const result = await genAI.models.generateContent({
        model: 'gemini-flash-latest',
        contents: fullPrompt
      });

      console.log('✅ Received response:', result);

      const text = result.text || 'No response received';
      console.log('📝 Response text:', text);

      const assistantMessage = {
        role: 'assistant',
        content: text,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      console.log('✅ Message added to chat');
    } catch (error) {
      console.error('❌ Error calling Gemini API:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      const errorMessage = {
        role: 'assistant',
        content: `Oops! Something went wrong: ${error.message}. Please try again.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      console.log('🏁 Message handling complete');
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col h-full bg-[#ece9d8]">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-[#0054e3] to-[#0099ff] text-white px-4 py-2 flex items-center border-b-2 border-[#003c74]">
        <img
          src="/vimarshpop.png"
          alt="AI Bot"
          className="w-6 h-6 mr-2 rounded-full"
          style={{ imageRendering: 'auto' }}
        />
        <div className="flex-1">
          <div className="font-bold text-sm">Vimarsh's AI Assistant</div>
          <div className="text-xs opacity-90">Online and ready to chat!</div>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{
          backgroundImage: 'url(/windowsxp.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-3 shadow-md ${message.role === 'user'
                  ? 'bg-[#d1e7ff] border border-[#0054e3]'
                  : 'bg-white border border-gray-300'
                }`}
            >
              <div className="flex items-start space-x-2">
                {message.role === 'assistant' && (
                  <img
                    src="/vimarshpop.png"
                    alt="Bot"
                    className="w-6 h-6 mt-1 rounded-full"
                    style={{ imageRendering: 'auto' }}
                  />
                )}
                <div className="flex-1">
                  <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {message.content}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatTime(message.timestamp)}
                  </div>
                </div>
                {message.role === 'user' && (
                  <img
                    src="/user.png"
                    alt="User"
                    className="w-6 h-6 mt-1"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-md">
              <div className="flex items-center space-x-2">
                <img
                  src="/vimarshpop.png"
                  alt="Bot"
                  className="w-6 h-6 rounded-full"
                  style={{ imageRendering: 'auto' }}
                />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-[#d4d0c8] border-t-2 border-[#ffffff] p-3">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message here..."
            className="flex-1 px-3 py-2 border-2 border-[#7f9db9] focus:outline-none focus:border-[#0054e3]"
            disabled={isLoading}
            style={{
              boxShadow: 'inset 1px 1px 0 0 #000000, inset -1px -1px 0 0 #ffffff'
            }}
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="bg-[#d4d0c8] border-2 border-[#ffffff] px-4 py-2 font-bold text-sm hover:bg-[#e8e5dd] active:border-[#808080] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{
              boxShadow: '2px 2px 0 0 #000000, -1px -1px 0 0 #808080, 1px 1px 0 0 #ffffff',
              borderTopColor: '#ffffff',
              borderLeftColor: '#ffffff',
              borderRightColor: '#808080',
              borderBottomColor: '#808080'
            }}
          >
            Send
          </button>
        </form>

        <div className="mt-2 text-xs text-gray-600 flex items-center justify-between">
          <span>💡 Try asking: "What is Hue?" or "Tell me about your experience at Waylo"</span>
          <span className="text-gray-500">Powered by Gemini 2.0</span>
        </div>
      </div>
    </div>
  );
};

export default AIBot;
